import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@/shared/ui/data-table';
import type { ConsignmentDisposition } from '@/entities/consignments';

import { useDispositionTable, createDispositionColumns, renderExpandIcon } from '../model';

interface DispositionTableProps {
  dispositions: ConsignmentDisposition[];
  isLoading?: boolean;
  isDownloadingLabels?: boolean;
  onDownloadLabels?: (o?: number) => void;
}

export const DispositionTable = memo(function DispositionTable({
  dispositions,
  isLoading,
  isDownloadingLabels,
  onDownloadLabels
}: DispositionTableProps) {
  const { t } = useTranslation();

  const { flatData, expandedRows, handleRowExpand, getRowChildren, getRowLevel, getRowClassName } =
    useDispositionTable(dispositions);

  const columns = useMemo(
    () => createDispositionColumns(t, isDownloadingLabels, onDownloadLabels),
    [t, isDownloadingLabels, onDownloadLabels]
  );

  if (isLoading) {
    return <div className="p-8 text-center text-muted-foreground animate-pulse">{t('common.loading')}</div>;
  }

  if (flatData.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground border-2 border-dashed rounded-lg">
        {t('common.noData')}
      </div>
    );
  }

  return (
    <DataTable
      data={flatData}
      columns={columns}
      tableId="disposition-table"
      hierarchical={true}
      expandedRows={expandedRows}
      onRowExpand={handleRowExpand}
      getRowChildren={getRowChildren}
      getRowLevel={getRowLevel}
      renderExpandIcon={renderExpandIcon}
      rowClassName={getRowClassName}
      enableColumnCustomization={false}
      enableFullscreen={false}
      enableExport={false}
      selectable={false}
      showToolbar={false}
      height="min-h-[400px]"
    />
  );
});
